import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLazyGetAdminSavedToursSessionQuery } from "../../../../../app/services/book/admin_saved_tours"
import { formatDate } from "../../../admin_extension_tokens/utils/date"

const buildSessionUrl = (sessionKey) =>
  `${window.location.origin}/book/saved_tours/session/${sessionKey}`

const SessionDetailModal = ({ sessionKey, onClose }) => {
  const { t } = useTranslation()
  const [fetchSession, { data, isFetching }] = useLazyGetAdminSavedToursSessionQuery()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (sessionKey) fetchSession(sessionKey)
  }, [sessionKey, fetchSession])

  if (!sessionKey) return null

  const session = data?.session
  const tours = session?.tours || []

  const handleOpen = () => {
    window.open(buildSessionUrl(sessionKey), "_blank", "noopener,noreferrer")
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildSessionUrl(sessionKey))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard unavailable
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-[#E7EAEF] max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E7EAEF] sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-[#1F2937]">
            {t("admin_saved_tours.detail.title")}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpen}
              className="text-xs font-medium text-[#1F2937] px-2.5 py-1.5 rounded-lg border border-[#E7EAEF] hover:bg-[#F4F6F9] transition-colors"
            >
              {t("admin_saved_tours.panel.open")}
            </button>
            <button
              onClick={handleCopy}
              className="text-xs font-medium text-[#1F2937] px-2.5 py-1.5 rounded-lg border border-[#E7EAEF] hover:bg-[#F4F6F9] transition-colors"
            >
              {copied
                ? t("admin_saved_tours.panel.copied")
                : t("admin_saved_tours.panel.copy_link")}
            </button>
            <button onClick={onClose} className="text-[#9AA5B4] hover:text-[#1F2937] text-lg leading-none px-1">
              ✕
            </button>
          </div>
        </div>

        {isFetching && !session && (
          <div className="text-center py-16 text-[#9AA5B4]">{t("admin_saved_tours.detail.loading")}</div>
        )}

        {session && (
          <div className="px-5 py-4">
            <div className="text-xs text-[#9AA5B4] mb-4">
              {session.company_name} · {session.user?.name || session.user?.email} ·{" "}
              {formatDate(session.created_at)}
            </div>

            <div className="flex flex-col gap-2">
              {tours.map((tour) => (
                <div key={tour.id} className="px-3 py-2.5 rounded-xl border border-[#E7EAEF]">
                  <div className="text-sm font-medium text-[#1F2937]">{tour.hotel_name}</div>
                  <div className="text-xs text-[#9AA5B4] mt-0.5">
                    {tour.region_name} · {tour.resort_name} · {tour.star_name}
                  </div>
                  <div className="text-xs text-[#9AA5B4] mt-0.5">
                    {tour.date_entry} — {tour.date_out} · {tour.nights} {t("admin_saved_tours.detail.nights")}
                  </div>
                  <div className="text-sm font-medium text-[#1F2937] mt-1">
                    {tour.final_price ?? tour.price} {tour.currency}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SessionDetailModal
